/**
 * This file contains data-only helpers for quiz-results.
 * The goal is to keep data transformation logic isolated from DOM rendering logic.
 */

/**
 * Safely parse JSON. Returns fallback value when JSON is invalid.
 *
 * @param {string | null} value
 * @param {any} fallback
 * @returns {any}
 */
export function safeJsonParse(value, fallback = null) {
  try {
    if (value === null) return fallback;
    return JSON.parse(value);
  } catch (_error) {
    return fallback;
  }
}

/**
 * Flatten Shopify selling plans from groups into a single list.
 * This mirrors the behavior from the Angular DataService.
 *
 * @param {any[]} sellingPlanGroups
 * @returns {any[]}
 */
export function consolidateSellingPlans(sellingPlanGroups) {
  return (sellingPlanGroups || []).flatMap((/** @type {any} */ group) =>
    (group?.selling_plans || []).map((/** @type {any} */ sellingPlan) => ({
      discount_amount: sellingPlan?.price_adjustments?.[0]?.value ?? 0,
      discount_type: sellingPlan?.price_adjustments?.[0]?.value_type ?? null,
      frequency: Number(sellingPlan?.name?.match(/\d+/)?.[0] ?? 0),
      selling_plan_id: String(sellingPlan?.id),
      title: sellingPlan?.name ?? '',
      unit: sellingPlan?.name?.toLowerCase().match(/day|week|month/)?.[0] ?? null,
    })),
  );
}

/**
 * Convert Octane product blocks into enriched Shopify product blocks.
 * Each mapped product includes:
 * - selected variant
 * - resolved title
 * - flattened selling plans
 *
 * @param {any} results
 * @param {any[]} compatibleProducts
 * @returns {any[][]}
 */
export function mapProductBlocks(results, compatibleProducts) {
  const blocks = Array.isArray(results?.product_blocks) ? results.product_blocks : [];
  const productsData = Array.isArray(compatibleProducts) ? compatibleProducts : [];



  return blocks.map((/** @type {any[]} */ productBlock) =>
    productBlock
      .map((/** @type {any} */ octaneProduct) => {

        let variantId = octaneProduct?.id;

        // Octane payload sometimes includes "variants" array.
        if (Array.isArray(octaneProduct?.variants) && octaneProduct.variants[0]) {
          variantId = octaneProduct.variants[0].id;
        }else if (octaneProduct?.variant_id) {
			// Add-on blocks send a flat "variant_id" instead of "variants".
			variantId = octaneProduct.variant_id;
		}

        const product = productsData.find(
          (/** @type {any} */ candidate) =>
            Array.isArray(candidate?.variants) &&
            candidate.variants.some((/** @type {any} */ variant) => variant.id === Number(variantId)),
        );
        if (!product) return null;

        const variant = product.variants.find((/** @type {any} */ item) => item.id === Number(variantId));
        if (!variant) return null;

        const variantTitle = String(variant.title || '').includes('Default') ? null : variant.title;



        return {
          ...product,
          title: octaneProduct?.title || variantTitle || product.title,
          variant,
          selling_plans: consolidateSellingPlans(product.selling_plan_groups),
        };
      })
      .filter(Boolean),
  );
}

/**
 * Build initial state payload from storage + globals.
 * This function does not touch DOM and can be tested independently.
 *
 * @param {Object} options
 * @param {string=} options.octaneResultsStorageKey
 * @param {string=} options.selectionStorageKey
 * @param {string=} options.resultsEmailStorageKey
 * @param {any[]=} options.compatibleProducts
 * @returns {any}
 */
export function buildInitialQuizDataState({
  octaneResultsStorageKey = 'g_octane_results',
  selectionStorageKey = 'g_quiz_results_selection',
  resultsEmailStorageKey = 'g_results_email',
  compatibleProducts = /** @type {any} */ (window).g_octane_compatible_products,
}) {
  const results = safeJsonParse(localStorage.getItem(octaneResultsStorageKey), null);
  const savedSelection = safeJsonParse(localStorage.getItem(selectionStorageKey), /** @type {any} */ ({})) || {};

  if (!results) {
    return {
      results: null,
      savedSelection,
      productBlocks: [],
      regimenProduct: null,
      visibleAddons: [],
      recommendedAddons: [],
      isReady: true,
    };
  }

  const productBlocks = mapProductBlocks(results, compatibleProducts);
  const regimenProduct = productBlocks?.[0]?.[0] || null;
  const visibleAddons = productBlocks?.[1] || [];
  const recommendedAddons = productBlocks?.[2] || [];



  // Keep compatibility with previous app behavior.
  localStorage.setItem(resultsEmailStorageKey, results.email || '');

  return {
    results,
    savedSelection,
    productBlocks,
    regimenProduct,
    visibleAddons,
    recommendedAddons,
    isReady: true,
  };
}
